import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import Table from "components/DesignSystem/Table/V2/Table";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import { ReferralEmptyState } from "components/icons/ReferralEmptyState";
import { TableEmptyState } from "components/TableEmptyState/TableEmptyState";
import { DISPLAY_DATE } from "constants/date";
import {
  FIRST_TASK_PAID,
  GROUP_ALREADY_CREATED,
  INVITED,
  NEW_GROUP_CREATED,
  PENDING,
  SENT,
  SIGNED_UP,
  SUBSCRIBED,
} from "constants/referral";
import dayjs from "dayjs";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCallback, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { MyReferrals, useGetReferralQuery } from "store/apis/refrral";

export const ReferralTable = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const [pageNum, setPageNum] = useState(1);
  const { data } = useGetReferralQuery(
    { groupId, pageNum },
    { skip: !groupId }
  );
  const { my_referrals = [] } = data || {};

  const noReferrals = my_referrals.length === 0;

  const createColumn = createColumnHelper<MyReferrals>();

  const getTagType = useCallback((status: string): TagColorType => {
    switch (status) {
      case INVITED:
        return "gray";
      case SIGNED_UP:
      case NEW_GROUP_CREATED:
      case GROUP_ALREADY_CREATED:
        return "blue";
      case SUBSCRIBED:
      case FIRST_TASK_PAID:
        return "green";
      default:
        return "orange";
    }
  }, []);

  const columns = [
    createColumn.accessor("referral_date", {
      header: "Date",
      size: 25,
      cell: (info) => <div>{dayjs(info.getValue()).format(DISPLAY_DATE)}</div>,
    }),

    createColumn.accessor("referee_email", {
      header: "Email",
      size: 35,
    }),

    createColumn.accessor("referral_status", {
      header: "Status",
      size: 25,
      cell: (info) => (
        <>
          {info.getValue() ? (
            <Tag rounded tagType={getTagType(info.getValue())}>
              {info.getValue()
                ? info.getValue().charAt(0).toUpperCase() +
                  info.getValue().slice(1).toLowerCase()
                : ""}
            </Tag>
          ) : (
            "-"
          )}
        </>
      ),
    }),
    createColumn.accessor("reward_status", {
      header: () => (
        <p className="t-m-0 t-text-right t-mr-0">Referral payout</p>
      ),
      size: 25,
      cell: (info) => {
        const value = info?.getValue();
        const tagType =
          value === PENDING ? "orange" : value === SENT ? "green" : "gray";
        return (
          <div className="t-text-body-sm t-font-medium t-text-right t-mr-0 t-flex t-justify-end">
            {info.getValue() !== "NA" ? (
              <Tag rounded tagType={tagType} icon={false}>
                {value
                  ? value?.charAt(0).toUpperCase() +
                    value?.slice(1).toLowerCase()
                  : ""}
              </Tag>
            ) : (
              "-"
            )}
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: data?.my_referrals || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!data || noReferrals) {
    return (
      <>
        <Table.Container>
          <Table.Content>
            <Table.Head>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.HeadCell
                      key={header.id}
                      style={{ width: `${header.getSize()}%` }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Table.HeadCell>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
          </Table.Content>
        </Table.Container>
        <div className="t-mt-[82px]">
          <TableEmptyState
            title="No referrals found"
            icon={<ReferralEmptyState />}
            description="When you refer someone, the status starts reflecting here"
          />
        </div>
      </>
    );
  }

  const { current_page, total_pages, per_page, total_count } = data;

  return (
    <div>
      <div className="t-py-2 t-flex t-items-center t-justify-end t-text-subtext t-text-text-100 -t-mb-2">
        <PaginationHandler
          onPageNumChange={(page) => setPageNum(page)}
          page_num={current_page}
          per_page={per_page}
          total_count={total_count}
          total_pages={total_pages}
        />
      </div>
      <Table.Container>
        <Table.Content>
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.HeadCell
                    key={header.id}
                    style={{ width: `${header.getSize()}%` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.HeadCell>
                ))}
              </Table.Row>
            ))}
          </Table.Head>
          <Table.Body>
            {table.getRowModel().rows.map((row) => (
              <Table.Row key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell
                    key={cell.id}
                    style={{ width: `${cell.column.getSize()}%` }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.Container>
    </div>
  );
};
